/**
 * F-0578 WAIT-12: every visible wait paints from one animation-frame loop. The loop runs only while
 * at least one wait is registered and the tab is visible; a wait registers while it is on screen.
 */

export type WaitingPainterType = (now: number) => void;

const painters = new Set<WaitingPainterType>();
let frame: number | null = null;
let listening = false;

function hidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function tick(now: number): void {
  frame = null;
  painters.forEach((paint) => paint(now));
  schedule();
}

function schedule(): void {
  if (frame !== null || painters.size === 0 || hidden()) return;
  frame = requestAnimationFrame(tick);
}

function stop(): void {
  if (frame !== null) cancelAnimationFrame(frame);
  frame = null;
}

function onVisibility(): void {
  if (hidden()) stop();
  else schedule();
}

/**
 * WAIT-13 (I-1689) and WAIT-12: what a wait is drawn on changes only when the root's theme, Accents
 * or class does. One observer counts those changes; a wait resolves its ground at mount and again
 * only when the count moves, never per frame.
 */
let groundChanges = 0;
let rootObserver: MutationObserver | null = null;

function watchRoot(): void {
  if (rootObserver || typeof MutationObserver === 'undefined' || typeof document === 'undefined') {
    return;
  }
  rootObserver = new MutationObserver(() => {
    groundChanges += 1;
  });
  rootObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'data-theme', 'data-accents'],
  });
}

/** How many times the root's theme, Accents or class has changed since the first wait drew. */
export function waitingGroundChanges(): number {
  return groundChanges;
}

export function registerWaitingPainter(paint: WaitingPainterType): () => void {
  watchRoot();
  painters.add(paint);
  if (!listening && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
    listening = true;
  }
  paint(performance.now());
  schedule();
  return () => {
    painters.delete(paint);
    if (painters.size === 0) stop();
  };
}

/** Tests only: how many waits paint, and whether a frame is scheduled. */
export function waitingLoopStateForTests(): { painters: number; running: boolean } {
  return { painters: painters.size, running: frame !== null };
}
